// The offline pack: the phone's copy of what it needs to fill forms in with
// no signal (round 42). Fetched whenever there is signal, kept per account.
//
// The screens show the phone's copy AT ONCE when there is one and swap in the
// fresh list when it arrives, rather than waiting on the network first: on
// one bar of signal a request can take twenty seconds to fail, and a crew
// member at 6:45 should not stare at "Loading…" for a list the phone already
// holds. When the copy is all there is, the screen says how old it is.
import { useEffect, useState } from 'react';
import { formFill, type OfflinePack } from '../formsApi';
import { offlineStore, type StoredPack } from './store';

/** Fetches a fresh pack and keeps it. Throws what the request threw. */
export async function refreshPack(owner: string): Promise<StoredPack> {
  const pack = await formFill.offlinePack();
  const stored: StoredPack = { owner, pack, savedAt: new Date().toISOString() };
  try {
    await offlineStore().putPack(stored);
  } catch {
    // A phone that cannot store still has the fresh list for this screen.
  }
  return stored;
}

export interface PackState {
  pack: OfflinePack | null;
  /** When this phone saved the copy on screen. */
  savedAt: string | null;
  /** True while the list on screen is the phone's copy, not a fresh one. */
  fromPhone: boolean;
  /** Set when there is no list at all: no signal and no copy. */
  error: string | null;
}

export function useFormsPack(owner: string | null): PackState {
  const [state, setState] = useState<PackState>({
    pack: null,
    savedAt: null,
    fromPhone: false,
    error: null,
  });

  useEffect(() => {
    if (!owner) return;
    let cancelled = false;
    let fresh = false;

    void offlineStore()
      .getPack(owner)
      .then((stored) => {
        if (cancelled || fresh || !stored) return;
        setState({ pack: stored.pack, savedAt: stored.savedAt, fromPhone: true, error: null });
      })
      .catch(() => undefined);

    void refreshPack(owner).then(
      (stored) => {
        fresh = true;
        if (cancelled) return;
        setState({ pack: stored.pack, savedAt: stored.savedAt, fromPhone: false, error: null });
      },
      async () => {
        const stored = await offlineStore().getPack(owner).catch(() => undefined);
        if (cancelled) return;
        setState(
          stored
            ? { pack: stored.pack, savedAt: stored.savedAt, fromPhone: true, error: null }
            : {
                pack: null,
                savedAt: null,
                fromPhone: false,
                error:
                  'Could not reach Dayboard, and this phone has no saved copy of your forms yet. Open Forms once with signal and they will be kept for next time.',
              }
        );
      }
    );

    return () => {
      cancelled = true;
    };
  }, [owner]);

  return state;
}
